'use client';

import { useCallback, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import type { VisibilityState } from '@tanstack/react-table';
import { useSettings } from '@/providers/settings-provider';
import { backfillEmployeesBatchAction, backfillWebsiteBatchAction } from '@/lib/actions/backfill';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import type { SearchExportOutcome } from '@/lib/search-export';
import { creditsUsedSnapshot, type CreditsUsedSnapshot } from '@/new_components/shared/CreditsUsedPopup';
import { exportToExcel } from './ResultsExportDialog';
import { DEFAULT_COLUMN_VISIBILITY, MOBILE_PAGE_SIZE, SWISS_DEFAULT_COLUMN_VISIBILITY, VISIBLE_COLUMNS_SETTING, VISIBLE_COLUMNS_SETTING_AT, VISIBLE_COLUMNS_SETTING_CH } from './resultsTableColumns';

// CompanyResultsTable's state and actions, one hook per toolbar concern. The
// table has no effects, so these only hold state and memoized callbacks.

export type GuestLockedFeature = 'export' | 'columns' | 'enrich';

/** The guest "account required" prompt and which feature opened it. */
export function useSignupDialog() {
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  // Which feature triggered the prompt — drives the dialog's description.
  const [signupDialogFeature, setSignupDialogFeature] =
    useState<GuestLockedFeature>('export');

  const openSignupDialog = useCallback((feature: GuestLockedFeature) => {
    setSignupDialogFeature(feature);
    setShowSignupDialog(true);
  }, []);
  return { showSignupDialog, setShowSignupDialog, signupDialogFeature, openSignupDialog };
}

interface ResultsExportInput {
  t: TFunction<'translation', undefined>;
  financialYear: number | undefined;
  country: string | undefined;
  canExport: boolean;
  fetchResultsForExport: ((rows: number) => Promise<SearchExportOutcome>) | undefined;
  onExportClick: (() => void) | undefined;
  onExportSuccess: ((info: { exportCount: number; columns: string[]; charged: number }) => void) | undefined;
  openSignupDialog: (feature: GuestLockedFeature) => void;
  /** Called when the export's credit cost cannot be paid. */
  onInsufficientCredits: () => void;
}

/**
 * Why the last export attempt did not produce a file. `file` means the rows
 * were fetched (and paid for) but the browser failed to write the .xlsx —
 * the same export again today is free, and the message says so.
 */
export type ExportErrorKind = 'failed' | 'file' | 'limit';

/**
 * The Excel export: the dialog's state, the sign-in-gated click and the
 * export run. The backend bills the export (1,000 credits, up to 10,000
 * rows) when it hands over the rows; the dialog states both before the
 * user confirms.
 */
export function useResultsExport({
  t,
  financialYear,
  country,
  canExport,
  fetchResultsForExport,
  onExportClick,
  onExportSuccess,
  openSignupDialog,
  onInsufficientCredits,
}: ResultsExportInput) {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<ExportErrorKind | null>(null);
  // "1,000 credits used, N left" after a paid export, or "free, already
  // paid today" for a repeat.
  const [creditsUsed, setCreditsUsed] = useState<CreditsUsedSnapshot | null>(null);

  const handleExportClick = useCallback(() => {
    // Log every click — guests included — before the sign-in gate, so we can
    // see demand for the export feature.
    onExportClick?.();
    if (!canExport) {
      openSignupDialog('export');
      return;
    }
    setExportError(null);
    setShowExportDialog(true);
  }, [canExport, onExportClick, openSignupDialog]);

  const runExport = useCallback(
    async (columnIds: string[], count: number) => {
      if (!fetchResultsForExport) return;
      setExportError(null);
      setIsExporting(true);
      let paid = false;
      try {
        const outcome = await fetchResultsForExport(count);
        if (outcome.kind === 'insufficient_credits') {
          setShowExportDialog(false);
          onInsufficientCredits();
          return;
        }
        if (outcome.kind === 'auth_required') {
          setShowExportDialog(false);
          openSignupDialog('export');
          return;
        }
        if (outcome.kind === 'limit_exceeded') {
          setExportError('limit');
          return;
        }
        paid = outcome.charged > 0 || outcome.entitled;
        await exportToExcel(outcome.rows, columnIds, t, financialYear, country);
        onExportSuccess?.({ exportCount: outcome.rows.length, columns: columnIds, charged: outcome.charged });
        setCreditsUsed(
          creditsUsedSnapshot(outcome.charged, outcome.balance, { entitled: outcome.entitled, export: true }),
        );
        setShowExportDialog(false);
      } catch {
        setExportError(paid ? 'file' : 'failed');
      } finally {
        setIsExporting(false);
      }
    },
    [fetchResultsForExport, financialYear, t, onExportSuccess, country, onInsufficientCredits, openSignupDialog],
  );
  const clearCreditsUsed = useCallback(() => setCreditsUsed(null), []);
  return {
    showExportDialog, setShowExportDialog, isExporting, exportError, handleExportClick, runExport,
    creditsUsed, clearCreditsUsed,
  };
}

/**
 * Column visibility. Only signed-in users may customize columns, so only they get
 * their persisted choice merged over the defaults; everyone else always sees
 * the defaults. `name`/`index` are forced visible so a stale stored value can
 * never hide the anchor columns. Defaults and the stored choice are both
 * per country scope: Swiss (CH) and Austrian (AT) each have their own stored
 * choice, DE and Global share. AT starts from the Swiss defaults — like the
 * Swiss register, the Firmenbuch import carries no financials yet.
 */
export function useColumnVisibility(country: string | undefined, canCustomizeColumns: boolean) {
  const { getOption, storeOption } = useSettings();
  const isSwiss = country === 'CH';
  const isAustrian = country === 'AT';
  const visibilitySetting = isAustrian
    ? VISIBLE_COLUMNS_SETTING_AT
    : isSwiss
      ? VISIBLE_COLUMNS_SETTING_CH
      : VISIBLE_COLUMNS_SETTING;
  const defaultVisibility = isSwiss || isAustrian
    ? SWISS_DEFAULT_COLUMN_VISIBILITY
    : DEFAULT_COLUMN_VISIBILITY;
  const storedVisibility = getOption<VisibilityState | undefined>(
    visibilitySetting,
  );
  const columnVisibility = useMemo<VisibilityState>(() => {
    const base = canCustomizeColumns
      ? { ...defaultVisibility, ...(storedVisibility ?? {}) }
      : { ...defaultVisibility };
    return { ...base, name: true, index: true };
  }, [canCustomizeColumns, storedVisibility, defaultVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (id: string, visible: boolean) => {
      storeOption(visibilitySetting, {
        ...columnVisibility,
        [id]: visible,
      });
    },
    [columnVisibility, storeOption, visibilitySetting],
  );
  return { columnVisibility, handleColumnVisibilityChange };
}

/** Header click → next sort: desc, then asc for toggle fields, else back to revenue-desc. */
export function useSortToggle(onSortChange: ((sort: string) => void) | undefined, sort: string | undefined) {
  return useCallback(
    (field: string) => {
      if (!onSortChange) return;

      const toggleFields = ['profit'];
      let newSort: string;
      if (sort === `${field}-desc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-asc`
          : 'revenue-desc';
      } else if (sort === `${field}-asc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-desc`
          : 'revenue-desc';
      } else {
        newSort = `${field}-desc`;
      }
      onSortChange(newSort);
    },
    [onSortChange, sort],
  );
}

interface ResultsEnrichInput {
  results: CompanySearchResult[];
  canEnrich: boolean;
  openSignupDialog: (feature: GuestLockedFeature) => void;
  onRefresh: (() => void) | undefined;
  onEmployeeCountsUpdated: ((counts: Record<string, number>) => void) | undefined;
}

/** The sign-in-gated website and employee enrich batch actions for the page on screen. */
export function useResultsEnrich({
  results,
  canEnrich,
  openSignupDialog,
  onRefresh,
  onEmployeeCountsUpdated,
}: ResultsEnrichInput) {
  const [isEnrichingWebsites, setIsEnrichingWebsites] = useState(false);
  const [isEnrichingEmployees, setIsEnrichingEmployees] = useState(false);

  const handleEnrichWebsites = useCallback(async () => {
    if (!canEnrich) {
      openSignupDialog('enrich');
      return;
    }
    const euIds = results.filter((r) => !r.domain).map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingWebsites(true);
    try {
      await backfillWebsiteBatchAction(euIds);
      onRefresh?.();
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingWebsites(false);
    }
  }, [results, canEnrich, openSignupDialog, onRefresh]);

  const handleEnrichEmployees = useCallback(async () => {
    if (!canEnrich) {
      openSignupDialog('enrich');
      return;
    }
    const euIds = results.map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingEmployees(true);
    try {
      const res = await backfillEmployeesBatchAction(euIds);
      // Overlay the freshly-parsed counts onto the displayed rows. We don't
      // call onRefresh here — that would re-read the (still stale) mv_search.
      if (res.success && res.results) {
        const counts: Record<string, number> = {};
        for (const r of res.results) {
          if (r.status !== 'ok' || !r.employee_data?.length) continue;
          // Latest year wins, matching how mv_search picks employee_count.
          const latest = r.employee_data
            .filter((d) => d.gesamt != null)
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
          if (latest?.gesamt != null) counts[r.eu_id] = latest.gesamt;
        }
        onEmployeeCountsUpdated?.(counts);
      }
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingEmployees(false);
    }
  }, [results, canEnrich, openSignupDialog, onEmployeeCountsUpdated]);
  return { isEnrichingWebsites, isEnrichingEmployees, handleEnrichWebsites, handleEnrichEmployees };
}

/** The rows the desktop grid and the mobile card list each show. */
export function useResultSlices(results: CompanySearchResult[], limit: number) {
  // Show up to the current page size (rows-per-page dropdown can raise it
  // above the old fixed 50).
  const displayedResults = useMemo(
    () => results.slice(0, limit),
    [results, limit],
  );
  // Mobile shows 10 cards per page. Slice independently so the brief window
  // before the mobile-limit-override useEffect fires (SSR limit is 50) doesn't
  // flash 50 cards.
  const mobileResults = useMemo(
    () => results.slice(0, MOBILE_PAGE_SIZE),
    [results],
  );
  return { displayedResults, mobileResults };
}
